import { useEffect, useState } from "react";
import { apiApont, type SeparacaoLinha, type SeparacaoTroca } from "../lib/api";
import { MsgModal } from "./MsgModal";
import { Modal, fN } from "./EstoqueUi";

// Separação do material da PCF — DECISÃO da reunião de 24/10/2026 (gestor + PCP). No FINALIZAR da etapa SEPARAÇÃO o PCP
// confirma o lote realmente usado; pode trocar por outro lote do mesmo produto com saldo livre. A gravação (trocas +
// confirmação) vai junto com a finalização da etapa, numa transação (servidor: pcf-separacao.ts). A baixa do material é no fim.

interface Props {
  numpcf: number;
  onConfirmar: (trocas: SeparacaoTroca[]) => void;
  onVoltar: () => void;
}

type Alt = { numlot: number; nomloc: string; unidade: string; comprimento: string; livre: number };
const casas = (u: string) => ((u ?? "").trim().toUpperCase() === "KG" ? 2 : 0);

export function SeparacaoMaterialModal({ numpcf, onConfirmar, onVoltar }: Props) {
  const [linhas, setLinhas] = useState<SeparacaoLinha[]>([]);
  const [nompro, setNompro] = useState("");
  const [troca, setTroca] = useState<Record<number, number>>({}); // id do empenho -> lote novo
  const [alts, setAlts] = useState<{ id: number; lotes: Alt[] } | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    apiApont.separacao(numpcf)
      .then((d) => {
        if (!d.ok) return setErro(d.erro ?? "Falha ao ler o material.");
        setLinhas(d.linhas);
        setNompro(d.nompro);
      })
      .catch((e) => setErro((e as Error).message));
  }, [numpcf]);

  async function abrirAlternativas(l: SeparacaoLinha) {
    try {
      const d = await apiApont.separacaoAlternativas(l.numlot);
      if (!d.ok) return setErro(d.erro ?? "Falha ao buscar lotes.");
      if (!d.lotes.length) return setErro(`Nenhum outro lote do mesmo produto com saldo livre para trocar o lote ${l.numlot}.`);
      setAlts({ id: l.id, lotes: d.lotes });
    } catch (e) {
      setErro((e as Error).message);
    }
  }

  const confirmar = () =>
    onConfirmar(Object.entries(troca).map(([id, numlot]) => ({ id: Number(id), numlot })));

  return (
    <Modal titulo="Separação do Material" sub={`PCF nº ${numpcf}${nompro ? " · " + nompro : ""}`} largura={980} onClose={onVoltar}>
      <MsgModal texto={erro} tipo="erro" onClose={() => setErro(null)} />
      <p className="pcf-hint" style={{ marginTop: 0 }}>
        Confirme o lote que vai para a máquina. Para usar outro lote, clique em Trocar. A baixa do estoque acontece no fim da produção.
      </p>
      <table className="tabela-pcfs">
        <thead>
          <tr>
            <th>Lote</th><th>Material</th><th>Local</th><th>Unid.</th><th>Compr.</th><th className="num">Qtd empenhada</th><th>Lote usado</th><th />
          </tr>
        </thead>
        <tbody>
          {linhas.length === 0 && (
            <tr><td colSpan={8}>Sem material empenhado.</td></tr>
          )}
          {linhas.map((l) => (
            <tr key={l.id}>
              <td>{l.numlot}</td>
              <td>{l.nompro}</td>
              <td>{l.nomloc}</td>
              <td>{l.unidade}</td>
              <td>{l.comprimento}</td>
              <td className="num">{fN(l.qtdemp, casas(l.unidade))}</td>
              <td><strong>{troca[l.id] ?? l.numlot}</strong></td>
              <td>
                {troca[l.id] ? (
                  <button className="btn-secundario" onClick={() => { const t = { ...troca }; delete t[l.id]; setTroca(t); }}>Desfazer</button>
                ) : (
                  <button className="btn-secundario" onClick={() => void abrirAlternativas(l)}>Trocar</button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="pcf-acoes" style={{ marginTop: 14 }}>
        <button className="btn-acao verde" autoFocus onClick={confirmar}>Confirmar separação e finalizar</button>
        <span className="spacer" />
        <button className="btn-secundario" onClick={onVoltar}>Voltar</button>
      </div>
      {alts && (
        <Modal titulo="Trocar lote" sub="Lotes do mesmo produto com saldo livre" largura={980} onClose={() => setAlts(null)}>
          <table className="tabela-pcfs">
            <thead>
              <tr><th>Lote</th><th>Local</th><th>Unid.</th><th>Compr.</th><th className="num">Saldo livre</th><th /></tr>
            </thead>
            <tbody>
              {alts.lotes.map((a) => (
                <tr key={a.numlot}>
                  <td>{a.numlot}</td>
                  <td>{a.nomloc}</td>
                  <td>{a.unidade}</td>
                  <td>{a.comprimento}</td>
                  <td className="num">{fN(a.livre, casas(a.unidade))}</td>
                  <td>
                    <button className="btn-acao" onClick={() => { setTroca({ ...troca, [alts.id]: a.numlot }); setAlts(null); }}>Usar este</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Modal>
      )}
    </Modal>
  );
}
